import { ComponentFactoryResolver, Injectable, ViewContainerRef } from '@angular/core';

import { FormItem } from '../../classes/form-item';
import { IFormComponent } from '../../models';

import {
    SearchComponent,
    SelectionComponent,
    MeetingTitleComponent,
    MeetingTypeDropdownComponent,
    UrlListComponent,
    DatePickerComponent,
    DateRangeComponent,
    QuickDatesComponent,
    DurationComponent,
    TimePickerComponent,
    SuggestionsComponent,
    BookingConfirmationComponent,
    ConfirmationComponent,
    CompletionComponent,
    LocationComponent,
    RoomComponent,
    GenericInputComponent,
    DividerComponent,
    VideoProviderDisplayComponent,
    BannerComponent,
    ExternalOnBehalfComponent,
    ReschedulePickerComponent,
    SpecificDatePickerComponent,
    MaxAttendeesComponent
} from '../../components';

import { FallbackComponent } from '../../components/common'; // NOTE: if we want to lazy load this, should add to WizardModule

export interface IFormOption {
    component: FormComponentName;
    classes?: string[];
    fieldSize?: string;
    data?: any;
}

export type FormComponentName =
    | 'search'
    | 'selection'
    | 'datepicker'
    | 'urllist'
    | 'banner'
    | 'meetingtitle'
    | 'meetingtype'
    | 'daterange'
    | 'dateintervals'
    | 'duration'
    | 'timepicker'
    | 'location'
    | 'room'
    | 'suggestions'
    | 'confirmation'
    | 'bookingconfirmation'
    | 'completion'
    | 'generic'
    | 'divider'
    | 'videoprovider'
    | 'externalonbehalf'
    | 'reschedulepicker'
    | 'specificdatepicker'
    | 'maxattendees'
    | 'fallback';

export type FormOptionsMap = { [path in FormComponentName]: any };

@Injectable({
    providedIn: 'root'
})
export class WizardBuilderService {
    private readonly formOptionsMap: FormOptionsMap = {
        search: SearchComponent,
        selection: SelectionComponent,
        datepicker: DatePickerComponent,
        urllist: UrlListComponent,
        banner: BannerComponent,
        meetingtitle: MeetingTitleComponent,
        meetingtype: MeetingTypeDropdownComponent,
        daterange: DateRangeComponent,
        dateintervals: QuickDatesComponent,
        duration: DurationComponent,
        timepicker: TimePickerComponent,
        location: LocationComponent,
        room: RoomComponent,
        suggestions: SuggestionsComponent,
        bookingconfirmation: BookingConfirmationComponent,
        confirmation: ConfirmationComponent,
        completion: CompletionComponent,
        generic: GenericInputComponent,
        divider: DividerComponent,
        videoprovider: VideoProviderDisplayComponent,
        externalonbehalf: ExternalOnBehalfComponent,
        fallback: FallbackComponent,
        reschedulepicker: ReschedulePickerComponent,
        specificdatepicker: SpecificDatePickerComponent,
        maxattendees: MaxAttendeesComponent
    };

    /**
     * TODO: ComponentFactoryResolver is no longer needed with v13
     */
    constructor(private componentFactoryResolver: ComponentFactoryResolver) {}

    /*
     * WIZARD COMPONENTS
     * The following methods are for building dynamic forms in the Wizard
     */

    /** @params formOptions comes from the back end as JSON 'instructions' */
    public getFormComponents(formOptions: IFormOption[]) {
        const selectedFormItems = formOptions.map((formOption: IFormOption) => {
            return this.getFormComponent(formOption);
        });

        return selectedFormItems;
    }

    public getFormComponent(formOption: IFormOption) {
        const component = this.formOptionsMap[formOption.component] || FallbackComponent;

        const selectFormItem = new FormItem(component, formOption.classes, formOption.fieldSize, formOption.data);

        return selectFormItem;
    }

    public resolveFormComponentsForView(formItems: FormItem[], viewContainerRef: ViewContainerRef) {
        formItems.forEach((formItem) => {
            return this.resolveFormComponentForView(formItem, viewContainerRef);
        });
    }

    /**
     * @param formItem contains details about the component to be rendered, including the component itself
     * @param viewContainerRef the node in the template to insert the new component. Can be a directive or ViewContainer
     */
    public resolveFormComponentForView(formItem: FormItem, viewContainerRef: ViewContainerRef) {
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(formItem.component);
        const componentRef = viewContainerRef.createComponent(componentFactory);

        (<IFormComponent>componentRef.instance).classes = formItem.classes;
        (<IFormComponent>componentRef.instance).fieldSize = formItem.fieldSize;
        (<IFormComponent>componentRef.instance).data = formItem.data;

        return componentFactory;
    }
}
